import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Archive } from 'lucide-react';
import { tools } from '../data/tools';

const archivedTools = tools.filter(t => t.status === 'archived');
const allTags = [...new Set(archivedTools.flatMap(t => t.tags))];

const ToolDemoArchivePage = () => {
  const [selectedTags, setSelectedTags] = useState([]);
  const toggleTag = (tag) => setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  const filteredTools = selectedTags.length === 0 ? archivedTools : archivedTools.filter(t => t.tags.some(tag => selectedTags.includes(tag)));

  return (
    <div className="min-h-screen pt-20" data-testid="tool-demo-archive-page">
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <Link to="/services/tool-demos" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--accent-cyan)] mb-6 font-mono text-sm"><ArrowRight className="w-4 h-4 rotate-180" /> Back to Tool Demos</Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 border border-[var(--text-muted)] bg-[var(--text-muted)]/10 flex items-center justify-center"><Archive className="w-8 h-8 text-[var(--text-muted)]" /></div>
              <div>
                <p className="font-mono text-sm uppercase tracking-wider text-[var(--text-muted)]">Legacy Tools</p>
                <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)]">Archived Tools</h1>
              </div>
            </div>
            <p className="text-[var(--text-muted)] text-lg md:text-xl leading-relaxed">Browse archived tool demos. These may be outdated but remain available for reference.</p>
          </div>
        </div>
      </section>

      {allTags.length > 0 && (
        <section className="py-8 border-y border-[var(--border)] ">
          <div className="container-main">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <span className="font-mono text-sm text-[var(--text-muted)] uppercase tracking-wider">Filter by Tag:</span>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (<button key={tag} onClick={() => toggleTag(tag)} className={`px-4 py-2 font-mono text-sm border transition-colors duration-200 ${selectedTags.includes(tag) ? 'border-[var(--text-muted)] bg-[var(--text-muted)]/20 text-[var(--text-main)]' : 'border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--text-muted)]/50'}`}>{tag}</button>))}
                {selectedTags.length > 0 && (<button onClick={() => setSelectedTags([])} className="px-4 py-2 font-mono text-sm border border-[var(--error)]/50 text-[var(--error)] hover:bg-[var(--error)]/10">Clear All</button>)}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-16">
        <div className="container-main">
          {filteredTools.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredTools.map((tool) => (
                <div key={tool.id} className="bg-[var(--surface)] border border-[var(--border)] overflow-hidden opacity-75 hover:opacity-100 transition-opacity" data-testid={`archived-tool-${tool.id}`}>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs text-[var(--text-muted)] font-mono uppercase">{tool.category}</span>
                      <div className="flex items-center gap-1"><Star className="w-4 h-4 text-[var(--text-muted)]" /><span className="text-sm text-[var(--text-muted)]">{tool.rating}</span></div>
                    </div>
                    <span className="inline-block px-2 py-1 bg-[var(--text-muted)]/20 text-[var(--text-muted)] text-xs font-mono uppercase mb-3">Archived</span>
                    <h3 className="text-xl font-bold text-[var(--text-main)] mb-2">{tool.name}</h3>
                    <p className="text-[var(--text-muted)] text-sm mb-4">{tool.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {tool.tags.map((tag, idx) => (<span key={idx} className="px-2 py-1 bg-[var(--surface-highlight)] border border-[var(--border)] text-xs text-[var(--text-muted)] font-mono">{tag}</span>))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Archive className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
              <p className="text-[var(--text-muted)] text-lg">No archived tools found.</p>
              <Link to="/services/tool-demos" className="mt-4 inline-block text-[var(--accent-cyan)] font-mono hover:underline">View Active Tools</Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ToolDemoArchivePage;
